import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Container, Box, Typography, Chip, TextField, MenuItem, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Tooltip,
} from '@mui/material'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import { getQorRestaurantDebts } from '../apollo/queries'
import { recordQorRestaurantDebt, settleQorRestaurantDebt } from '../apollo/mutations'

const GET_DEBTS = gql`${getQorRestaurantDebts}`
const RECORD_DEBT = gql`${recordQorRestaurantDebt}`
const SETTLE_DEBT = gql`${settleQorRestaurantDebt}`

const INK = '#1c2024'
const MUTED = '#697177'
const LINE = '#e8eaed'
const OWED = '#e5484d'      // debt red
const RECOVERED = '#17a34a' // recovery green

// How each ledger entry kind is rendered — the sign convention is from the
// platform's point of view: DEBT = they owe us (+), everything else pays it down (−).
const KIND_META = {
  DEBT: { label: 'Debt', bg: '#fdebec', fg: '#a12226', sign: '+' },
  RECOVERY: { label: 'Recovered from split', bg: '#e9f6ee', fg: '#166534', sign: '−' },
  REPAYMENT: { label: 'Manual repayment', bg: '#e7f2fd', fg: '#0b5394', sign: '−' },
  WAIVE: { label: 'Waived', bg: '#f0f0f3', fg: '#5a5c66', sign: '−' },
}

const usd = (v) => `$${Number(v || 0).toFixed(2)}`

export default function QorDebts() {
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState('')
  // Settle dialog state: { restaurantId, restaurantName, outstanding } or null.
  const [settle, setSettle] = useState(null)
  const [settleAmount, setSettleAmount] = useState('')
  const [settleKind, setSettleKind] = useState('REPAYMENT')
  const [settleNote, setSettleNote] = useState('')
  // Manual debt dialog state.
  const [manualOpen, setManualOpen] = useState(false)
  const [manualRestaurantId, setManualRestaurantId] = useState('')
  const [manualAmount, setManualAmount] = useState('')
  const [manualNote, setManualNote] = useState('')

  const { data, loading, refetch } = useQuery(GET_DEBTS, {
    variables: { limit: 200, offset: 0 },
    fetchPolicy: 'network-only',
  })

  const rows = data?.qorRestaurantDebts?.rows ?? []
  const outstanding = data?.qorRestaurantDebts?.outstanding ?? []
  const totalOwed = outstanding.reduce((a, o) => a + Number(o.outstanding || 0), 0)

  const [settleMutation, { loading: settling }] = useMutation(SETTLE_DEBT, {
    onCompleted: () => {
      setNotice('Recorded.'); setError(''); setSettle(null)
      setSettleAmount(''); setSettleNote('')
      setTimeout(() => setNotice(null), 4000)
      refetch()
    },
    onError: (e) => setError(e.message),
  })
  const [recordDebtMutation, { loading: recording }] = useMutation(RECORD_DEBT, {
    onCompleted: () => {
      setNotice('Debt recorded.'); setError(''); setManualOpen(false)
      setManualRestaurantId(''); setManualAmount(''); setManualNote('')
      setTimeout(() => setNotice(null), 4000)
      refetch()
    },
    onError: (e) => setError(e.message),
  })

  const openSettle = (o, kind) => {
    setSettleKind(kind)
    setSettleAmount(String(o.outstanding))
    setSettleNote('')
    setSettle(o)
  }

  return (
    <Box sx={{ bgcolor: '#f7f8fa', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2.5, display: 'grid', placeItems: 'center',
            background: `linear-gradient(135deg, ${OWED}, #f5a524)`, color: '#fff',
          }}>
            <AccountBalanceWalletIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: INK, lineHeight: 1.1 }}>Restaurant Debts</Typography>
            <Typography variant="body2" sx={{ color: MUTED }}>
              Delivery-fallback accounting — what restaurants owe RushmyFood, and how it&apos;s being recovered from their split share.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" sx={{ ml: 'auto' }} onClick={() => setManualOpen(true)}>
            Add manual debt
          </Button>
        </Box>

        {notice && <Alert severity="success" sx={{ my: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {error && <Alert severity="error" sx={{ my: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Outstanding balances */}
        <Box sx={{ display: 'flex', gap: 1.5, my: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '0 0 200px', borderRadius: 3, p: 2, bgcolor: '#fff', border: `1px solid ${LINE}` }}>
            <Typography variant="caption" sx={{ color: MUTED }}>Total outstanding</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: totalOwed > 0 ? OWED : RECOVERED }}>
              {usd(totalOwed)}
            </Typography>
          </Box>
          {outstanding.map((o) => (
            <Box key={o.restaurantId} sx={{
              flex: '0 0 auto', minWidth: 220, borderRadius: 3, p: 2, bgcolor: '#fff',
              border: `1px solid ${LINE}`, borderTop: `3px solid ${OWED}`,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: INK }}>{o.restaurantName || `Restaurant ${o.restaurantId}`}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: OWED }}>{usd(o.outstanding)}</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
                <Tooltip title="They paid us outside the splits (check, transfer…)">
                  <Button size="small" variant="outlined" onClick={() => openSettle(o, 'REPAYMENT')}>Record repayment</Button>
                </Tooltip>
                <Tooltip title="Forgive part or all of this balance">
                  <Button size="small" variant="text" color="inherit" onClick={() => openSettle(o, 'WAIVE')}>Waive</Button>
                </Tooltip>
              </Box>
            </Box>
          ))}
          {outstanding.length === 0 && !loading && (
            <Box sx={{ flex: 1, borderRadius: 3, p: 2, bgcolor: '#fff', border: `1px dashed ${LINE}`, display: 'grid', placeItems: 'center' }}>
              <Typography variant="body2" sx={{ color: MUTED }}>No restaurant currently owes anything. 🎉</Typography>
            </Box>
          )}
        </Box>

        {/* Ledger */}
        <Box sx={{ borderRadius: 3, border: `1px solid ${LINE}`, bgcolor: '#fff', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5, borderBottom: `1px solid ${LINE}` }}>
            <Typography sx={{ fontWeight: 700, color: INK }}>Ledger</Typography>
            <Typography variant="caption" sx={{ color: MUTED }}>every entry, newest first — debits and credits tied to their orders</Typography>
          </Box>
          {loading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress size={26} /></Box>
          ) : rows.length === 0 ? (
            <Typography variant="body2" sx={{ color: MUTED, p: 3 }}>
              No entries yet. A debt appears automatically when a restaurant-delivery order
              is charged (fee + tip credited to the restaurant) but ends up delivered by a
              RushmyFood rider or First Delivery.
            </Typography>
          ) : rows.map((r) => {
            const meta = KIND_META[r.kind] || { label: r.kind, bg: '#f0f0f3', fg: MUTED, sign: '' }
            return (
              <Box key={r._id} sx={{
                display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25,
                borderBottom: `1px solid ${LINE}`, '&:last-child': { borderBottom: 'none' },
                flexWrap: 'wrap',
              }}>
                <Typography variant="caption" sx={{ color: MUTED, width: 150 }}>
                  {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: INK, width: 160 }}>
                  {r.restaurantName || `Restaurant ${r.restaurantId}`}
                </Typography>
                <Chip size="small" label={meta.label} sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700, borderRadius: 1.5 }} />
                <Typography variant="body2" sx={{
                  fontWeight: 800, color: r.kind === 'DEBT' ? OWED : RECOVERED,
                  fontVariantNumeric: 'tabular-nums', width: 90,
                }}>
                  {meta.sign}{usd(r.amount)}
                </Typography>
                <Typography variant="caption" sx={{ color: MUTED, width: 110 }}>
                  {r.orderNumber ? `Order ${r.orderNumber}` : (r.orderId ? `Order #${r.orderId}` : '—')}
                </Typography>
                <Typography variant="caption" sx={{ color: MUTED, flex: 1, minWidth: 200 }}>
                  {r.note || '—'}
                </Typography>
              </Box>
            )
          })}
        </Box>

        <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 3 }}>
          Recoveries happen automatically: on each new split transaction, up to the outstanding
          balance is withheld from the restaurant&apos;s share (oldest debt first) until cleared.
          The withheld amount shows on the Transaction Splits page for that order.
        </Typography>

        {/* Settle dialog */}
        <Dialog open={!!settle} onClose={() => setSettle(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>
            {settleKind === 'WAIVE' ? 'Waive debt' : 'Record repayment'} — {settle?.restaurantName || ''}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            <Typography variant="body2" sx={{ color: MUTED }}>
              Outstanding: <b>{usd(settle?.outstanding)}</b>. Applied to the oldest debts first;
              every entry stays in the ledger for the audit trail.
            </Typography>
            <TextField
              label="Amount" size="small" value={settleAmount} type="number"
              onChange={(e) => setSettleAmount(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01, max: settle?.outstanding }}
            />
            <TextField
              label="Note (why / how)" size="small" value={settleNote} multiline minRows={2}
              onChange={(e) => setSettleNote(e.target.value)}
              placeholder={settleKind === 'WAIVE' ? 'e.g. goodwill for repeated app issue' : 'e.g. paid by e-transfer 07/06'}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSettle(null)}>Cancel</Button>
            <Button
              variant="contained" disabled={settling || !(parseFloat(settleAmount) > 0)}
              color={settleKind === 'WAIVE' ? 'inherit' : 'primary'}
              onClick={() => settleMutation({
                variables: {
                  restaurantId: settle.restaurantId,
                  amount: parseFloat(settleAmount),
                  kind: settleKind,
                  note: settleNote || undefined,
                },
              })}
            >
              {settling ? 'Saving…' : (settleKind === 'WAIVE' ? 'Waive' : 'Record repayment')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manual debt dialog — for fallbacks the automatic hook missed. */}
        <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Add manual debt</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            <Typography variant="body2" sx={{ color: MUTED }}>
              Records that a restaurant owes RushmyFood money (e.g. a delivery fallback the
              system missed). It will be recovered from their future split share automatically.
            </Typography>
            <TextField
              label="Restaurant ID" size="small" value={manualRestaurantId} type="number"
              onChange={(e) => setManualRestaurantId(e.target.value)}
              helperText="The numeric id from the Restaurants page"
            />
            <TextField
              label="Amount" size="small" value={manualAmount} type="number"
              onChange={(e) => setManualAmount(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Reason / note" size="small" value={manualNote} multiline minRows={2}
              onChange={(e) => setManualNote(e.target.value)}
              placeholder="e.g. order 2-00431 delivered by our rider, fee+tip were in their share"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={recording || !manualRestaurantId || !(parseFloat(manualAmount) > 0)}
              onClick={() => recordDebtMutation({
                variables: {
                  restaurantId: manualRestaurantId,
                  amount: parseFloat(manualAmount),
                  note: manualNote || undefined,
                },
              })}
            >
              {recording ? 'Saving…' : 'Record debt'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}